import { IsNotEmpty, IsPositive, Max, ValidateIf, IsDefined, Validate, ValidateNested } from 'class-validator';
import { computed } from 'mobx';

import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model, BookemZulu } from 'src/core/services/data/JayDataContext';
import { DisplayNameValidation } from 'src/core/customValidator/displayNameValidator';
import { ContractsProductValidator } from 'src/core/customValidator/contracts-product-validator';
import { TourItineraryProductValidator } from 'src/core/customValidator/tour-itinerary-product-validator';
import { TourItineraryDateValidator } from 'src/core/customValidator/tour-itinerary-date-validator';

export class TourItineraryDataService extends DataService<typeof Model.Products.Logic.ViewModels.TourItineraryItem, Model.Products.Logic.ViewModels.TourItineraryItem, TourItineraryItem> {
	protected map(entity: Model.Products.Logic.ViewModels.TourItineraryItem): TourItineraryItem {
		return new TourItineraryItemModel(entity);
	}

	static make(context?: any): TourItineraryDataService {
		return new TourItineraryDataService(context, ctx => ctx.TourItineraries);
	}

	static id = -1;

	add(defaultLanguage: string, product: ProductItem): Promise<TourItineraryItem> {
		const entity = new Model.Products.Logic.ViewModels.TourItineraryItem();
		entity.id = TourItineraryDataService.id--;
		entity.createdOn = new Date(); // used in itinerary product durations
		if (product) {
			entity.product = product.entity;
			if (product.id > 0) {
				entity.productId = product.id;
			}
		}

		entity.products = [];
		entity.dates = [];
		entity.contracts = [];
		(entity as any).dynamicProperties['displayName'] = {
			[defaultLanguage]: ''
		};
		(entity as any).dynamicProperties['translations'] = {
			[defaultLanguage]: ''
		};
		(entity as any).dynamicProperties['weekdays'] = [false, false, false, false, false, false, false]
		return this.create(() => entity);
	}
}

export class TourItineraryItemModel extends EntityModel<Model.Products.Logic.ViewModels.TourItineraryItem, TourItineraryItem> implements TourItineraryItem {	
	constructor(entity: Model.Products.Logic.ViewModels.TourItineraryItem) {
		super(entity);
	}

	@property<TourItineraryItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;

	@property<TourItineraryItemModel>(o => o.entity.productId, (o, value) => o.entity.productId = value)
	public productId: number;

	@property<TourItineraryItemModel>(o => o.entity.product, (o, value) => o.entity.product = value)
	public product: any;

	@property<TourItineraryItemModel>(o => o.entity.productServiceTypeId, (o, value) => o.entity.productServiceTypeId = value)
	@IsDefined()
	public productServiceTypeId: number;

	@property<TourItineraryItemModel>(o => o.entity.productServiceTypeDisplayName, (o, value) => o.entity.productServiceTypeDisplayName = value)
	public productServiceTypeDisplayName: string;

	@property<any>(o => o.entity.dynamicProperties, (o, value) => o.entity.dynamicProperties = value)
	@Validate(DisplayNameValidation, ['en'])
	public dynamicProperties: {};

	//@property<TourItineraryItemModel>(o => o.entity.contracts, (o, value) => o.entity.contracts = value)
	@ValidateNested()
	@Validate(ContractsProductValidator)
	public contracts: ContractItem[];

	@property<TourItineraryItemModel>(o => o.entity.products, (o, value) => o.entity.products = value)
	@Validate(TourItineraryProductValidator)
	@ValidateNested()
	public products: TourItineraryProductItem[];

	@property<TourItineraryItemModel>(o => o.entity.dates, (o, value) => o.entity.dates = value)
	@Validate(TourItineraryDateValidator)
	@ValidateNested()
	public dates: TourItineraryDateItem[];

	async validate(validateChildren: boolean, property?: string, excludeProperties?: string[]): Promise<ValidationError[]> {
		let result = await super.validate(validateChildren, property);
		if (validateChildren && this.contracts) {
			for (let contract of this.contracts) {
				if (contract.allotment && contract.allotment.validate) {
					const allotmentErrors = await contract.allotment.validate(true);
					result = [...result, ...allotmentErrors];
				}
			}
		}

		if (excludeProperties) {
			result = result.filter(m => excludeProperties.every(p => p !== m.property));
		}

		return result;
	}
}



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/form/tour-itinerary.data.ts